import React, { useCallback, useEffect } from 'react';
import { Picker, StyleSheet, View } from 'react-native';
import Button from "../../../components/Button";
import AdminInput from "../../../components/AdminInput";
import AdminDatePicker from "../../../components/AdminDatePicker";
import { useForm, Controller } from 'react-hook-form';
import CurrencyInput from "react-native-currency-input";
import { fixedExpirationDateValidator } from "../../../utils/validators";
import {
  FORM,
  Field,
  FieldLabel,
  FieldError,
  Hint,
  Row,
  Col,
  SectionCard,
  SegmentedControl,
  InfoBanner,
  pickerStyle,
} from "./formStyles";

const EXPIRATION_OPTIONS = [
  {
    value: 'days',
    label: 'Dias após a compra',
    description: 'Cada aluno perde o acesso N dias após a compra.',
    icon: 'time-outline',
  },
  {
    value: 'fixedDate',
    label: 'Data fixa de fim de acesso',
    description: 'Todos os alunos perdem o acesso na mesma data.',
    icon: 'calendar-outline',
  },
];

const CreateProductGenericForm = ({ onClose, onCreate, saveHandler, initialProduct }) => {
  const [loadingSave, setLoadingSave] = React.useState(false);

  const { control, handleSubmit, setValue, watch, formState: { errors }, setError, clearErrors } = useForm({
    defaultValues: {
      name: initialProduct?.name || null,
      description: initialProduct?.description || null,
      type: initialProduct?.type || 'product',
      maxFrequency: 0,
      hasExpirationDate: true,
      archived: false,
      paymentFractions: 1,
      trialDays: 0,
      expirationType: initialProduct?.expirationType || 'days',
      // Pré-preenche os valores do modo herdado do produto (fluxo "Nova oferta");
      // sem isso o admin é forçado a redigitar e pode divergir do produto.
      expirationDate: initialProduct?.expirationDate != null ? String(initialProduct.expirationDate) : undefined,
      fixedExpirationDate: initialProduct?.fixedExpirationDate || undefined,
    },
  });

  const watchType = watch('type');
  const watchFrequency = watch('frequency');
  const watchPaymentFractions = watch('paymentFractions');
  const watchExpirationType = watch('expirationType');

  const onSubmit = useCallback(async (formData) => {
    try {
      setLoadingSave(true);
      // Mutual exclusivity: submit only the selected mode's value, never both.
      const payload = { ...formData };
      if (payload.expirationType === 'fixedDate') {
        payload.expirationDate = null;
      } else {
        payload.expirationType = 'days';
        payload.fixedExpirationDate = null;
      }
      saveHandler(payload)
        .finally(() => {
          onClose()
          setLoadingSave(false)
        });
    } catch (error) {
      onClose();
    }
  }, [onCreate, onClose]);

  useEffect(() => {
    if (watchType === 'plan') {
      setValue('frequency', "1")
      setValue('maxFrequency', "0")
      setValue('trialDays', "0")
    }
  }, [watchType]);

  useEffect(() => {
    if (!watchFrequency || !watchPaymentFractions) {
      return;
    }

    if (Number(watchFrequency) < watchPaymentFractions) {
      setValue('paymentFractions', watchFrequency);
    }

  }, [watchFrequency])

  const getValidationRules = (field, message) => {
    const rules = { required: `${message} é obrigatório` };

    if (field === 'frequency' || field === 'trialDays') {
      if (watchType === 'plan') {
        return rules;
      }
    }

    if (field === 'expirationDate') {
      // days input is only shown/validated in days mode; plan keeps it optional.
      if (watchExpirationType !== 'days' || watchType === 'plan') {
        return { required: false };
      }
    }

    return rules;
  };

  const renderPaymentFractionItems = () => {
    let maxFractions = watchType !== 'plan' ? 12 : Math.min(12, Number(watchFrequency));

    return Array.from({ length: maxFractions }, (_, i) => (
      <Picker.Item key={i + 1} label={`${i + 1}`} value={`${i + 1}`} />
    ));
  };

  return (
    <View style={styles.container}>
      {!initialProduct && (
        <SectionCard title="Produto" subtitle="Informações básicas exibidas ao aluno.">
          <Row>
            <Col>
              <Field>
                <FieldLabel tooltip="Nome do produto.">Nome</FieldLabel>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    ...getValidationRules('name', 'Nome do Produto'),
                    validate: (value) => /^[a-zA-Z0-9 ]*$/.test(value) || 'Apenas letras, números são permitidos',
                  }}
                  render={({ field }) =>
                    <>
                      <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} />
                      <FieldError>{errors.name?.message}</FieldError>
                    </>
                  }
                />
              </Field>
            </Col>
            <Col>
              <Field>
                <FieldLabel tooltip="Produto para venda única / Assinatura para venda recorrente.">Tipo</FieldLabel>
                <Controller
                  name="type"
                  control={control}
                  rules={getValidationRules('type', 'Tipo')}
                  render={({ field }) =>
                    <>
                      <Picker {...field} selectedValue={field.value} style={pickerStyle}>
                        <Picker.Item label="Assinatura (recorrente)" value="plan" />
                        <Picker.Item label="Produto (venda única)" value="product" />
                      </Picker>
                      <FieldError>{errors.type?.message}</FieldError>
                    </>
                  }
                />
              </Field>
            </Col>
          </Row>

          <Field>
            <FieldLabel tooltip="Descrição do produto/assinatura.">Descrição</FieldLabel>
            <Controller
              name="description"
              control={control}
              rules={{
                ...getValidationRules('description', 'Descrição'),
                validate: (value) => /^[a-zA-Z0-9 ]*$/.test(value) || 'Apenas letras, números são permitidos',
              }}
              render={({ field }) =>
                <>
                  <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} maxLength={50} />
                  <FieldError>{errors.description?.message}</FieldError>
                </>
              }
            />
          </Field>

          <Field style={{ marginBottom: 0 }}>
            <FieldLabel tooltip="Escolha entre validade por número de dias após a compra OU uma data fixa de fim de acesso (a mesma para todos os alunos)." tooltipHeight={110}>
              Modo de validade do acesso
            </FieldLabel>

            {watchType === 'product' && (
              <InfoBanner variant="warn">
                Produtos (venda única) precisam de uma validade de acesso — dias após a compra OU data fixa.
                Sem isso, a compra não libera o conteúdo e os alunos ficam com os cadeados.
              </InfoBanner>
            )}

            <Controller
              name="expirationType"
              control={control}
              render={({ field }) =>
                <SegmentedControl
                  options={EXPIRATION_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                />
              }
            />

            {watchExpirationType === 'days' && (
              <View style={{ marginTop: 16 }}>
                <FieldLabel>Dias de validade após a compra</FieldLabel>
                <Controller
                  name="expirationDate"
                  control={control}
                  rules={{
                    ...getValidationRules('expirationDate', 'Dias de Validade após a Compra'),
                    // Produto em modo "dias" exige um inteiro >= 1: 0/vazio deixaria
                    // o produto sem expiração e o webhook NÃO liberaria o acesso.
                    validate: (value) => {
                      if (watchType === 'plan' || watchExpirationType !== 'days') return true;
                      const n = Number(value);
                      return (Number.isInteger(n) && n >= 1)
                        || 'Informe um número de dias maior ou igual a 1 (senão o conteúdo fica bloqueado após a compra).';
                    },
                  }}
                  render={({ field }) => <>
                    <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} keyboardType="numeric" placeholder="Ex.: 365" />
                    <FieldError>{errors.expirationDate?.message}</FieldError>
                  </>
                  }
                />
                <InfoBanner variant="warn">
                  Apenas defina uma validade em dias se este produto for uma venda única (one-time purchase).
                </InfoBanner>
              </View>
            )}

            {watchExpirationType === 'fixedDate' && (
              <View style={{ marginTop: 16 }}>
                <FieldLabel>Data fixa de fim de acesso</FieldLabel>
                <Controller
                  name="fixedExpirationDate"
                  control={control}
                  rules={{ validate: (value) => fixedExpirationDateValidator(value) === '' || fixedExpirationDateValidator(value) }}
                  render={({ field }) => <>
                    <AdminDatePicker
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FieldError>{errors.fixedExpirationDate?.message}</FieldError>
                  </>
                  }
                />
                <InfoBanner variant="info">
                  Esta data de fim de acesso é a mesma para todos os alunos, independente de quando compraram.
                </InfoBanner>
              </View>
            )}
          </Field>
        </SectionCard>
      )}

      <SectionCard title="Oferta" subtitle="Preço e condições de pagamento.">
        <Row>
          <Col>
            <Field style={{ marginBottom: 0 }}>
              <FieldLabel tooltip="Preço do produto em Reais.">Preço</FieldLabel>
              <Controller
                name="price"
                control={control}
                rules={getValidationRules('price', 'Preço')}
                render={({ field }) =>
                  <>
                    <CurrencyInput
                      value={field.value}
                      onChangeValue={(value) => field.onChange(value)}
                      prefix="R$ "
                      delimiter="."
                      separator=","
                      precision={2}
                      minValue={0}
                      maxValue={99999.99}
                      style={styles.currencyInput}
                    />
                    <FieldError>{errors.price?.message}</FieldError>
                  </>
                }
              />
            </Field>
          </Col>

          <Col>
            <Field style={{ marginBottom: 0 }}>
              <FieldLabel tooltip="Número limite de parcelas. Ex.: 12 permite parcelar em até 12x; 1 apenas à vista. Juros por parcela são repassados ao aluno automaticamente." tooltipHeight={90}>
                Número máximo de parcelas
              </FieldLabel>
              <Controller
                name="paymentFractions"
                control={control}
                rules={getValidationRules('paymentFractions', 'Numero de Parcelas')}
                render={({ field }) =>
                  <>
                    <Picker {...field} selectedValue={field.value} style={pickerStyle}>
                      {renderPaymentFractionItems()}
                    </Picker>
                    <FieldError>{errors.paymentFractions?.message}</FieldError>
                  </>
                }
              />
            </Field>
          </Col>
        </Row>
      </SectionCard>

      {watchType === 'plan' && (
        <SectionCard title="Assinatura" subtitle="Regras de cobrança recorrente.">
          <Row>
            <Col>
              <Field>
                <FieldLabel tooltip="Determina a frequência de cobrança recorrente em meses.">Cobrar a cada (meses)</FieldLabel>
                <Controller
                  name="frequency"
                  control={control}
                  rules={getValidationRules('frequency', 'Frequência de cobrança em meses')}
                  render={({ field }) =>
                    <>
                      <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} keyboardType="numeric" />
                      <FieldError>{errors.frequency?.message}</FieldError>
                    </>
                  }
                />
              </Field>
            </Col>

            <Col>
              <Field>
                <FieldLabel tooltip="Número máximo de cobranças. 0 = assinatura renovada indefinidamente; qualquer outro valor cancela a assinatura após o número de ciclos escolhido." tooltipHeight={90}>
                  Ciclo máximo de cobranças
                </FieldLabel>
                <Controller
                  name="maxFrequency"
                  control={control}
                  render={({ field }) =>
                    <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} keyboardType="numeric" />
                  }
                />
              </Field>
            </Col>
          </Row>

          <Field style={{ marginBottom: 0 }}>
            <FieldLabel tooltip="(Opcional) Quantidade de dias em que o aluno terá acesso ao conteúdo antes de qualquer cobrança.">
              Dias de teste grátis
            </FieldLabel>
            <Controller
              name="trialDays"
              control={control}
              rules={getValidationRules('trialDays', 'Dias de teste grátis')}
              render={({ field }) =>
                <>
                  <AdminInput value={field.value} onChangeText={field.onChange} onBlur={field.onBlur} keyboardType="numeric" />
                  <FieldError>{errors.trialDays?.message}</FieldError>
                </>
              }
            />
          </Field>
        </SectionCard>
      )}

      <View style={styles.footer}>
        <Button
          title="Salvar produto"
          isLoading={loadingSave}
          onPress={handleSubmit(onSubmit)}
          colorbutton={FORM.accent}
          colortitle="#ffffff"
          width="100%"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: FORM.surfaceSubtle,
    padding: 16,
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
  },
  currencyInput: {
    height: 48,
    marginTop: 6,
    borderWidth: 1,
    borderColor: FORM.border,
    borderRadius: 10,
    paddingHorizontal: 12,
    backgroundColor: FORM.surface,
    color: FORM.ink,
    fontSize: 15,
  },
  footer: {
    marginTop: 4,
    marginBottom: 8,
  },
});

export default CreateProductGenericForm;
